/**
 * The application shell: navigation, identity, and the acting organization.
 *
 * Two rules hold this together.
 *
 * **Navigation shows what you may reach, and nothing that does not exist yet.**
 * Two separate facts decide that, and `navigation.ts` keeps them separate: the
 * decision point — not this code — says whether a principal may use a built
 * capability, and the registry says whether the capability is built at all.
 * Areas the platform has not built are simply absent. A greyed-out link to a
 * screen that does not exist is a promise the product has not made, and hiding
 * one by pretending it was forbidden would be a lie about the reason.
 *
 * **Primary navigation offers workspaces, not every destination.** A flat list
 * of eligible screens makes an administrative page and a whole area of the
 * product look like peers, and it gives a principal granted one capability a
 * two-item application. So the sidebar offers Home and the workspaces with
 * something in them; the capabilities inside one are reached from its hub, and
 * the breadcrumb says which workspace and cluster the current page sits in.
 * Deep routes stay exactly where they were — a bookmark, a refresh and a pasted
 * link must all still work.
 *
 * Eligibility itself is not computed here. `eligibleWorkspaces` is the single
 * implementation, shared with the hubs and the breadcrumb, because three
 * versions of "may this be offered" would eventually disagree.
 *
 * **The organization is a server-side selection.** Choosing one posts to the
 * API and reloads what the session is; it does not filter a list in the browser,
 * because the acting organization is part of every authorization decision.
 *
 * That last point decides the wording, and the wording was wrong. The engine
 * matches an `ORGANIZATION`-scoped policy only when `query.organization_id` is
 * not null and equals the resource's — so acting with *no* organization
 * selected is the **narrowest** setting available, not the broadest: every
 * organization-scoped grant stops matching and only tenant-scoped ones remain.
 * Calling that "All organizations" told the operator they had widened their
 * reach at the exact moment they narrowed it, which is the kind of sentence
 * somebody acts on before discovering it was false.
 */

import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, signOut } from "../api/client";
import type { OrganizationMembership } from "../api/types";
import { useSession } from "../session/SessionProvider";
import { OrgIcon } from "./icons";
import { eligibleWorkspaces, locate } from "./navigation";

/**
 * What to call an organization the caller belongs to.
 *
 * The name is the server's (`NXP-IDENTITY-ORG-NAME-001`): `/identity/me` now
 * projects `organization_memberships`, each carrying the `display_name` of the
 * identity `organization` row the membership points at. This function chooses
 * between names it was given; it does not go looking for one.
 *
 * That distinction is the whole point. Joining to
 * `/core/entities?entity_type=organization` would put a name here too, and it
 * would be the wrong one — that registry's Organization is a canonical
 * *entity*, this one is an identity `organization` a membership points at, and
 * they are different objects with different identifiers. A wrong name beside
 * the authority it scopes is worse than no name at all.
 *
 * The fallback is the old behaviour and should never be reached: the server
 * names every membership it lists. It stays because being unable to name
 * something is a reason to show the identifier as the identifier it is —
 * shortened for width, with the whole value in `title` — and never a reason to
 * dress a truncated UUID up as a name.
 */
function organizationLabel(id: string, memberships: OrganizationMembership[]): string {
  return memberships.find((m) => m.organization_id === id)?.display_name ?? id.slice(0, 8);
}

function OrganizationPicker() {
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const { principal } = session;

  if (principal.organization_ids.length === 0) return null;

  return (
    <>
      <label className="nx-pill nx-pill--quiet" style={{ gap: 8, padding: "6px 12px" }}>
        <OrgIcon className="nx-nav__icon" />
        <span className="nx-visually-hidden">Acting organization</span>
        <select
          className="nx-select"
          style={{ border: 0, background: "transparent", padding: 0 }}
          value={principal.active_organization_id ?? ""}
          disabled={busy}
          aria-describedby="nx-org-scope"
          onChange={(event) => {
            const value = event.target.value;
            setBusy(true);
            void api
              .post("/identity/organization", { organization_id: value === "" ? null : value })
              .finally(() => {
                setBusy(false);
                // The acting organization is part of every authorization
                // decision, so what the session may do is re-read, not adjusted.
                session.reload();
              });
          }}
        >
          {/* Not "All organizations". Selecting nothing removes every
              organization-scoped grant; it does not span them. */}
          <option value="">No organization</option>
          {/* Iterated from `organization_ids`, not from the names: an
              organization the caller belongs to must be offered even if it
              somehow arrived unnamed. Ordered by what is shown, because a list
              of names in identifier order reads as no order at all.

              The value stays the identifier — it is what the selection posts
              back, and what every authorization decision is made against. Only
              the label is a name, and `title` keeps the whole identifier
              reachable for anyone matching this against the record. */}
          {principal.organization_ids
            .map((id) => ({
              id,
              label: organizationLabel(id, principal.organization_memberships),
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ id, label }) => (
              <option key={id} value={id} title={id}>
                {label}
              </option>
            ))}
        </select>
      </label>
      {/* Outside the label on purpose. A `label` that wraps its control takes
          its whole text content as the control's accessible name, so leaving
          these inside would make the name the label *and* the description read
          out as one run-on string. */}
      <span className="nx-visually-hidden" id="nx-org-scope">
        {principal.active_organization_id === null
          ? "No organization is selected, so only tenant-wide grants apply. Grants scoped to an organization do not."
          : "Decisions are made as this organization. Grants scoped to another organization do not apply."}
      </span>
      <span className="nx-visually-hidden" role="status" aria-live="polite">
        {busy ? "Changing the acting organization" : ""}
      </span>
    </>
  );
}

/** Initials, for the identity block. Two at most, from what the platform gave us. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase() || "?";
}

/**
 * Where the current page sits, named so arriving somewhere deep still explains
 * itself.
 *
 * Suppressed when the destination *is* the workspace — Home would otherwise
 * read "Home / Work / Home" — and absent entirely on a hub, where the page
 * heading already carries the workspace name.
 */
function Breadcrumb({ pathname, may }: { pathname: string; may: (capability: string) => boolean }) {
  const where = locate(pathname, may);
  if (where === null || where.destination.to === where.workspace.to) return null;
  return (
    <nav className="nx-crumbs" aria-label="Breadcrumb">
      <ol>
        <li>
          <Link to={where.workspace.to}>{where.workspace.label}</Link>
        </li>
        <li aria-hidden="true" className="nx-crumbs__sep">
          /
        </li>
        <li>{where.cluster}</li>
        <li aria-hidden="true" className="nx-crumbs__sep">
          /
        </li>
        <li aria-current="page">{where.destination.label}</li>
      </ol>
    </nav>
  );
}

export function AppShell({ title, children }: { title: string; children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const workspaces = eligibleWorkspaces(session.may);

  return (
    <div className={`nx-shell${navOpen ? " nx-shell--nav-open" : ""}`}>
      <a className="nx-skip" href="#nx-main">
        Skip to content
      </a>
      <nav className="nx-sidebar" id="nx-sections" aria-label="Sections">
        <div className="nx-brand">
          {/* The wordmark the brand system ships, not an initial in a box. */}
          <img className="nx-brand__wordmark" src="/brand/taevas-wordmark-white.png" alt="Taevas" />
          <span className="nx-brand__product">Nexus Platform</span>
        </div>
        <div className="nx-nav">
          {workspaces.map((workspace) => {
            const current =
              workspace.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(workspace.to) ||
                  workspace.groups.some((group) =>
                    group.items.some(
                      (item) => item.to !== "/" && location.pathname.startsWith(item.to),
                    ),
                  );
            return (
              // A plain link, not a `NavLink`: which workspace you are in is a
              // registry question, not a path-prefix one. `/enterprise/graph`
              // is in Platform and does not start with Platform's own route,
              // and `NavLink` would overwrite the answer with its own.
              <Link
                key={workspace.key}
                to={workspace.to}
                className="nx-nav__item"
                aria-current={current ? "page" : undefined}
                // Where the navigation is a panel over the page, following a
                // link must not leave it covering what it just opened. Closed
                // here rather than in an effect on the path, because choosing a
                // workspace you are already in should close it too.
                onClick={() => setNavOpen(false)}
              >
                <span className="nx-nav__label">{workspace.label}</span>
                {/* Not a permission, and not a promise: how many capabilities
                    in this workspace the decision point permitted just now. */}
                <span className="nx-nav__count" aria-hidden="true">
                  {workspace.groups.reduce((total, group) => total + group.items.length, 0)}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="nx-main">
        <header className="nx-topbar">
          <button
            type="button"
            className="nx-navtoggle"
            aria-expanded={navOpen}
            aria-controls="nx-sections"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span className="nx-visually-hidden">
              {navOpen ? "Hide navigation" : "Show navigation"}
            </span>
            <span aria-hidden="true">☰</span>
          </button>
          <div className="nx-topbar__where">
            <Breadcrumb pathname={location.pathname} may={session.may} />
            <h1 className="nx-topbar__title">{title}</h1>
          </div>
          <span className="nx-topbar__spacer" />
          <OrganizationPicker />
          <div className="nx-identity">
            <span className="nx-avatar" aria-hidden="true">
              {initialsOf(session.principal.display_name)}
            </span>
            <span className="nx-identity__who">
              <span className="nx-identity__name">{session.principal.display_name}</span>
              <span className="nx-hint">
                {session.principal.used_multifactor ? "Multifactor" : "Single factor"}
              </span>
            </span>
          </div>
          <button className="nx-button" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </header>
        <main className="nx-content" id="nx-main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
